import { useEffect, useState } from "react";
import api from "@/lib/api";
import { TICKET_STATUS, TICKET_CATEGORIES, TICKET_PRIORITY } from "@/lib/tickets";
import { formatDate } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Wrench, ChatCircleDots, Clock, PaperPlaneTilt, Paperclip, Image as ImageIcon, Trash, FileArrowUp } from "@phosphor-icons/react";
import { toast } from "sonner";
import MessagingDialog from "@/components/MessagingDialog";

export default function Tickets() {
  const [items, setItems] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [properties, setProperties] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [statusFilter, setStatusFilter] = useState("active");

  const load = async () => {
    const params = {};
    if (statusFilter && statusFilter !== "active" && statusFilter !== "all") params.status = statusFilter;
    const { data } = await api.get("/tickets", { params });
    const filtered = statusFilter === "active"
      ? data.filter((t) => !["done", "closed"].includes(t.status))
      : data;
    setItems(filtered);
  };

  const loadMeta = async () => {
    const [{ data: c }, { data: p }] = await Promise.all([
      api.get("/contacts"),
      api.get("/properties"),
    ]);
    setContacts(c);
    setProperties(p);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter]);
  useEffect(() => { loadMeta(); }, []);

  const grouped = {};
  Object.keys(TICKET_STATUS).forEach((s) => { grouped[s] = []; });
  items.forEach((t) => { (grouped[t.status] || (grouped[t.status] = [])).push(t); });

  return (
    <div className="space-y-4" data-testid="tickets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Tickets</h1>
          <p className="text-sm text-slate-500 mt-1">Anfragen und Schadensmeldungen verwalten</p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-44 h-9" data-testid="ticket-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Nur aktive</SelectItem>
              <SelectItem value="all">Alle</SelectItem>
              {Object.entries(TICKET_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 gap-2" data-testid="new-ticket-btn">
                <Plus size={16} /> Neues Ticket
              </Button>
            </DialogTrigger>
            <TicketDialog contacts={contacts} properties={properties}
              onSaved={() => { setOpen(false); load(); }} />
          </Dialog>
        </div>
      </div>

      {/* Kanban-style columns */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 min-w-0">
        {["new", "in_progress", "waiting_supplier", "done"].map((status) => {
          const s = TICKET_STATUS[status];
          const list = grouped[status] || [];
          return (
            <div key={status} className="bg-white border border-slate-200 rounded-sm min-h-[400px]">
              <div className="px-4 py-2.5 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${s.dot}`}></span>
                  <span className="text-sm font-semibold text-slate-800">{s.label}</span>
                </div>
                <span className="text-xs text-slate-500 tabular-nums">{list.length}</span>
              </div>
              <div className="p-2 space-y-2">
                {list.length === 0 && (
                  <div className="text-xs text-slate-400 text-center py-8">Keine Tickets</div>
                )}
                {list.map((t) => {
                  const p = TICKET_PRIORITY[t.priority] || TICKET_PRIORITY.medium;
                  return (
                    <button
                      key={t.id}
                      onClick={() => setActive(t)}
                      className="w-full text-left bg-white border border-slate-200 hover:border-slate-400 rounded-sm p-3 transition-colors"
                      data-testid={`ticket-card-${t.id}`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1.5">
                        <div className="text-[10px] font-mono text-slate-500">{t.ticket_number}</div>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${p.className}`}>{p.label}</span>
                      </div>
                      <div className="text-sm font-medium text-slate-900 leading-tight">{t.title}</div>
                      <div className="text-xs text-slate-500 mt-1.5 line-clamp-2">{t.description}</div>
                      <div className="flex items-center gap-3 mt-2 text-[10px] text-slate-500">
                        {t.unit_number && <span className="font-mono">{t.unit_number}</span>}
                        {t.reporter_name && <span>· {t.reporter_name}</span>}
                        {t.comments?.length > 0 && (
                          <span className="flex items-center gap-0.5 ml-auto"><ChatCircleDots size={11} />{t.comments.length}</span>
                        )}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {active && (
        <TicketDetail
          ticket={active}
          contacts={contacts}
          onClose={() => setActive(null)}
          onChanged={() => { load(); setActive(null); }}
        />
      )}
    </div>
  );
}

function TicketDialog({ contacts, properties, onSaved, defaults = {} }) {
  const [form, setForm] = useState({
    title: "", description: "",
    category: "other", priority: "medium",
    reporter_contact_id: "", tenancy_id: "", property_id: "", unit_id: "",
    ...defaults,
  });
  const [units, setUnits] = useState([]);
  const [tenantTenancies, setTenantTenancies] = useState([]);
  const [loadingTenancies, setLoadingTenancies] = useState(false);
  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  useEffect(() => {
    if (!form.property_id) { setUnits([]); return; }
    api.get(`/properties/${form.property_id}`).then((r) => setUnits(r.data.units || []));
  }, [form.property_id]);

  // 2026-08 — Bei Mieter-Auswahl: alle MVs (aktiv+beendet) laden.
  //   Ein MV → sofort automatisch Property/Unit/Tenancy setzen.
  //   Mehrere MVs → User wählt aus (Auswahl-Dropdown erscheint).
  useEffect(() => {
    if (!form.reporter_contact_id) {
      setTenantTenancies([]);
      return;
    }
    setLoadingTenancies(true);
    api.get(`/tenancies?tenant_contact_id=${form.reporter_contact_id}`)
      .then(({ data }) => {
        const items = Array.isArray(data) ? data : (data.items || []);
        setTenantTenancies(items);
        if (items.length === 1) {
          // Auto-Fill bei genau einem MV
          const t = items[0];
          setForm((s) => ({
            ...s,
            tenancy_id: t.id,
            property_id: t.property_id || "",
            unit_id: t.unit_id || "",
          }));
        } else if (items.length === 0) {
          // Kein MV → auch tenancy_id zurücksetzen, damit später leer bleibt
          setForm((s) => ({ ...s, tenancy_id: "" }));
        } else {
          setForm((s) => ({ ...s, tenancy_id: "", property_id: "", unit_id: "" }));
        }
      })
      .catch(() => setTenantTenancies([]))
      .finally(() => setLoadingTenancies(false));
  }, [form.reporter_contact_id]);

  // Wenn User manuell aus dem Auswahl-Dropdown ein MV auswählt → Property/Unit
  // synchronisieren.
  const pickTenancy = (tenancyId) => {
    const t = tenantTenancies.find((x) => x.id === tenancyId);
    if (!t) return;
    setForm((s) => ({
      ...s,
      tenancy_id: t.id,
      property_id: t.property_id || "",
      unit_id: t.unit_id || "",
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/tickets", {
        ...form,
        reporter_contact_id: form.reporter_contact_id || null,
        tenancy_id: form.tenancy_id || null,
        property_id: form.property_id || null,
        unit_id: form.unit_id || null,
      });
      toast.success("Ticket angelegt");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const tenantContacts = contacts.filter((c) => (c.roles || []).includes("tenant"));
  const showTenancyPicker = tenantTenancies.length > 1;

  const tenancyLabel = (t) => {
    const status = t.status === "active" && !t.contract_end ? "aktiv"
      : (t.contract_end ? `beendet ${new Date(t.contract_end).toLocaleDateString("de-DE")}` : t.status);
    return `${t.contract_number || "?"} · ${t.property_name || ""} · ${t.unit_label || ""} · ${status}`;
  };

  return (
    <DialogContent className="max-w-xl">
      <DialogHeader>
        <DialogTitle>Neues Ticket</DialogTitle>
        <DialogDescription>Anliegen, Schaden oder Anfrage erfassen.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-3">
        <F label="Titel"><Input required value={form.title} onChange={(e) => set("title", e.target.value)} data-testid="ticket-title" /></F>
        <F label="Beschreibung"><Textarea rows={3} value={form.description} onChange={(e) => set("description", e.target.value)} /></F>
        <div className="grid grid-cols-2 gap-3">
          <F label="Kategorie">
            <Select value={form.category} onValueChange={(v) => set("category", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TICKET_CATEGORIES).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Priorität">
            <Select value={form.priority} onValueChange={(v) => set("priority", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TICKET_PRIORITY).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>

        {/* 2026-08 — Melder (Mieter) zuerst: löst Auto-Fill des MV/Objekts aus */}
        <F label="Melder (Mieter)">
          <Select value={form.reporter_contact_id} onValueChange={(v) => set("reporter_contact_id", v)}>
            <SelectTrigger data-testid="ticket-reporter"><SelectValue placeholder="Mieter auswählen (optional)" /></SelectTrigger>
            <SelectContent>
              {tenantContacts.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.display_name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.company_name || "—"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </F>

        {/* Mehrere MVs → Auswahl anbieten (auch beendete MVs sind wählbar) */}
        {showTenancyPicker && (
          <F label={`Mietverhältnis (${tenantTenancies.length} vorhanden — bitte auswählen)`}>
            <Select value={form.tenancy_id} onValueChange={pickTenancy}>
              <SelectTrigger data-testid="ticket-tenancy"><SelectValue placeholder="Mietverhältnis wählen" /></SelectTrigger>
              <SelectContent>
                {tenantTenancies.map((t) => (
                  <SelectItem key={t.id} value={t.id}>{tenancyLabel(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
        )}
        {/* Ein einziges MV → automatisch verlinkt, Info-Banner */}
        {form.reporter_contact_id && tenantTenancies.length === 1 && (
          <div className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-2 py-1.5" data-testid="ticket-auto-tenancy">
            Automatisch verknüpft: {tenancyLabel(tenantTenancies[0])}
          </div>
        )}
        {form.reporter_contact_id && !loadingTenancies && tenantTenancies.length === 0 && (
          <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            Für diesen Kontakt sind keine Mietverhältnisse hinterlegt — bitte Objekt/Einheit manuell wählen.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <F label="Objekt">
            <Select value={form.property_id} onValueChange={(v) => set("property_id", v)}>
              <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.property_number} – {p.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
          <F label="Einheit">
            <Select value={form.unit_id} onValueChange={(v) => set("unit_id", v)} disabled={!form.property_id}>
              <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent>
                {units.map((u) => <SelectItem key={u.id} value={u.id}>{u.unit_number}</SelectItem>)}
              </SelectContent>
            </Select>
          </F>
        </div>
        <DialogFooter>
          <Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="ticket-save-btn">Anlegen</Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

function F({ label, children, className = "" }) {
  return (
    <div className={className}>
      <Label className="text-xs text-slate-700">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function TicketDetail({ ticket, contacts, onClose, onChanged }) {
  const [data, setData] = useState(ticket);
  const [comment, setComment] = useState("");
  const [isInternal, setIsInternal] = useState(false);
  // 2026-08 — Schreibdialog direkt aus dem Ticket heraus
  const [messagingOpen, setMessagingOpen] = useState(false);

  useEffect(() => { setData(ticket); }, [ticket]);

  const changeStatus = async (status) => {
    try {
      await api.patch(`/tickets/${data.id}`, { ...data, status });
      toast.success("Status aktualisiert");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const sendComment = async () => {
    if (!comment.trim()) return;
    try {
      await api.post(`/tickets/${data.id}/comments`, { text: comment, is_internal: isInternal });
      const r = await api.get(`/tickets/${data.id}`);
      setData(r.data);
      setComment("");
      toast.success("Kommentar hinzugefügt");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const convertToVorgang = async () => {
    if (data.converted_to_vorgang_id) {
      // Bereits weitergeleitet — direkt öffnen
      window.location.href = `/vorgaenge/${data.converted_to_vorgang_id}`;
      return;
    }
    if (!await window._domivusConfirm(`Ticket ${data.ticket_number || ""} in einen neuen Vorgang weiterleiten?\n\nDas Ticket wird auf „geschlossen" gesetzt, ein neuer Vorgang mit demselben Titel und Kontext wird angelegt.`)) return;
    try {
      const { data: res } = await api.post(`/tickets/${data.id}/convert-to-vorgang`);
      toast.success(`Vorgang ${res.case_number} angelegt`);
      onChanged();
      // Direkt zum neu erstellten Vorgang navigieren
      window.location.href = `/vorgaenge/${res.vorgang_id}`;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler beim Weiterleiten");
    }
  };

  const status = TICKET_STATUS[data.status];
  const priority = TICKET_PRIORITY[data.priority];

  // 2026-02 — Wenn das Ticket automatisch aus einem Schaden entstand
  // (Titel beginnt mit „[SCH-YYYY-NNNN] …"), Link auf die Schaden-Detailseite
  // anbieten. Der User kann damit von hier direkt zur Dokumentenmappe und
  // zum „Neues Schreiben"-Button auf der Schaden-Detailseite springen.
  const damageMatch = /^\[?(SCH-\d{4}-\d{4})\]?/i.exec(data.title || "");
  const linkedDamageNumber = damageMatch ? damageMatch[1] : null;

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono text-slate-500">{data.ticket_number}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${status.className}`}>{status.label}</span>
            <span className={`text-[10px] px-2 py-0.5 rounded-full ${priority.className}`}>{priority.label}</span>
            {linkedDamageNumber && (
              <button
                type="button"
                onClick={async () => {
                  try {
                    // Bevorzugt via ticket_id — sonst über die Schadennummer
                    let d = null;
                    try {
                      const { data: r1 } = await api.get(`/damages?ticket_id=${data.id}&limit=1`);
                      d = (r1.items || [])[0];
                    } catch {}
                    if (!d) {
                      const { data: r2 } = await api.get(`/damages?damage_number=${encodeURIComponent(linkedDamageNumber)}&limit=1`);
                      d = (r2.items || [])[0];
                    }
                    if (d?.id) window.location.href = `/schaden/${d.id}`;
                    else toast.error("Schaden nicht gefunden");
                  } catch (e) { toast.error("Schaden-Link fehlgeschlagen"); }
                }}
                className="text-[10px] uppercase tracking-wider bg-amber-100 text-amber-800 hover:bg-amber-200 px-2 py-1 rounded-full inline-flex items-center gap-1"
                data-testid="ticket-open-damage"
                title="Zur Schaden-Detailseite mit Dokumentenmappe und Schreiben-Editor"
              >
                → {linkedDamageNumber} öffnen
              </button>
            )}
            {data.converted_to_vorgang_id ? (
              <a
                href={`/vorgaenge/${data.converted_to_vorgang_id}`}
                className="ml-auto text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-800 hover:bg-emerald-200 px-2 py-1 rounded-full inline-flex items-center gap-1"
                data-testid="ticket-open-vorgang"
              >
                Vorgang {data.converted_to_case_number || ""} öffnen →
              </a>
            ) : (
              <div className="ml-auto flex items-center gap-1.5">
                {data.reporter_contact_id && (
                  <Button
                    size="sm"
                    className="h-7 text-xs gap-1.5 bg-emerald-700 hover:bg-emerald-800 text-white"
                    onClick={() => setMessagingOpen(true)}
                    data-testid="ticket-reply-tenant"
                    title="Direkt an den Melder schreiben — Kontext & Referenz werden vorbelegt"
                  >
                    <PaperPlaneTilt size={12} /> An Mieter antworten
                  </Button>
                )}
                <Button
                  size="sm" variant="outline"
                  className="h-7 text-xs gap-1.5"
                  onClick={convertToVorgang}
                  data-testid="ticket-convert-vorgang"
                >
                  In Vorgang weiterleiten
                </Button>
              </div>
            )}
          </div>
          <DialogTitle>{data.title}</DialogTitle>
          <DialogDescription className="text-slate-600">{TICKET_CATEGORIES[data.category]} · {data.reporter_name} · {data.unit_number || "—"}</DialogDescription>
        </DialogHeader>

        <div className="bg-slate-50 border border-slate-200 rounded-sm p-3 text-sm text-slate-700 whitespace-pre-wrap">
          {data.description || "Keine Beschreibung."}
        </div>

        <div className="flex flex-wrap gap-1.5">
          {Object.entries(TICKET_STATUS).map(([k, v]) => (
            <Button
              key={k} size="sm" variant={data.status === k ? "default" : "outline"}
              onClick={() => changeStatus(k)}
              className={`h-7 text-xs ${data.status === k ? "bg-slate-900 text-white" : ""}`}
              data-testid={`ticket-set-${k}`}
            >
              {v.label}
            </Button>
          ))}
        </div>

        <div className="space-y-2">
          <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><ChatCircleDots size={14} /> Kommentare</h3>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(data.comments || []).length === 0 && <div className="text-xs text-slate-500">Keine Kommentare.</div>}
            {(data.comments || []).map((c) => (
              <div key={c.id} className={`p-2.5 rounded-sm border text-sm ${c.is_internal ? "bg-amber-50 border-amber-200" : "bg-white border-slate-200"}`}>
                <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
                  <span>{c.author_name || "Unbekannt"}{c.is_internal && " · INTERN"}</span>
                  <span>{formatDate(c.created_at)}</span>
                </div>
                <div className="text-slate-800 whitespace-pre-wrap">{c.text}</div>
              </div>
            ))}
          </div>
          <div className="flex gap-2 pt-2">
            <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Kommentar..." data-testid="ticket-comment-input" />
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 text-xs text-slate-600">
              <input type="checkbox" checked={isInternal} onChange={(e) => setIsInternal(e.target.checked)} data-testid="ticket-comment-internal" />
              Interne Notiz (Mieter sehen das nicht)
            </label>
            <Button size="sm" onClick={sendComment} className="bg-slate-900 hover:bg-slate-800 h-8" data-testid="ticket-comment-send">Senden</Button>
          </div>
        </div>

        {/* 2026-08 — Bilder & Anlagen (max 5 × 5 MB) */}
        <TicketAttachmentsPanel ticket={data} onChanged={async () => {
          const r = await api.get(`/tickets/${data.id}`);
          setData(r.data);
        }} />
      </DialogContent>

      {/* 2026-08 — Direkt aus dem Ticket an den Melder schreiben.
           Empfänger-Kette wird automatisch vorbelegt: Mieter (reporter_contact_id).
           Betreff-Präfix inkl. Ticket-Nummer. Antwort wird im Ticket-Kommentar vermerkt. */}
      {messagingOpen && (
        <MessagingDialog
          open={messagingOpen}
          onOpenChange={(v) => {
            setMessagingOpen(v);
            if (!v) onChanged();
          }}
          context={{
            contact_id: data.reporter_contact_id,
            tenancy_id: data.tenancy_id || undefined,
            property_id: data.property_id || undefined,
            source: "ticket",
            ticket_id: data.id,
          }}
          defaultSubject={`[Ticket ${data.ticket_number || ""}] ${data.title}`}
        />
      )}
    </Dialog>
  );
}


// 2026-08 — Ticket-Anlagen (Bilder & PDFs, max 5 × 5 MB)
function TicketAttachmentsPanel({ ticket, onChanged }) {
  const [uploading, setUploading] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  // Blob-URLs pro Attachment — via api-Client (Bearer-Token) statt reines <img src=".../api/">
  const [blobs, setBlobs] = useState({});
  const atts = ticket.attachments || [];
  const MAX = 5;
  const MAX_MB = 5;

  useEffect(() => {
    const stale = { ...blobs };
    let alive = true;
    (async () => {
      for (const att of atts) {
        if (blobs[att.id]) continue;
        try {
          const { data } = await api.get(
            `/tickets/${ticket.id}/attachments/${att.id}`, { responseType: "blob" },
          );
          if (!alive) return;
          setBlobs((b) => ({ ...b, [att.id]: URL.createObjectURL(data) }));
        } catch { /* silent */ }
      }
    })();
    return () => {
      alive = false;
      // Alte Blob-URLs freigeben
      Object.values(stale).forEach((u) => u && URL.revokeObjectURL(u));
    };
  }, [ticket.id, atts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const upload = async (files) => {
    if (!files || !files.length) return;
    if (atts.length + files.length > MAX) {
      return toast.error(`Maximal ${MAX} Anlagen pro Ticket`);
    }
    setUploading(true);
    let ok = 0;
    for (const f of Array.from(files)) {
      if (f.size > MAX_MB * 1024 * 1024) {
        toast.error(`${f.name}: zu groß (max ${MAX_MB} MB)`);
        continue;
      }
      const fd = new FormData();
      fd.append("file", f);
      try {
        await api.post(`/tickets/${ticket.id}/attachments`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        ok++;
      } catch (e) {
        toast.error(`${f.name}: ${e?.response?.data?.detail || "Upload fehlgeschlagen"}`);
      }
    }
    setUploading(false);
    if (ok > 0) {
      toast.success(`${ok} Anlage(n) hochgeladen`);
      onChanged();
    }
  };

  const del = async (attId) => {
    if (!await window._domivusConfirm("Anlage löschen?")) return;
    try {
      await api.delete(`/tickets/${ticket.id}/attachments/${attId}`);
      toast.info("Anlage gelöscht");
      onChanged();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Fehler");
    }
  };

  const urlFor = (att) => blobs[att.id] || null;

  return (
    <div className="space-y-2 pt-3 border-t border-slate-200" data-testid="ticket-attachments-panel">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5">
          <Paperclip size={14} /> Bilder &amp; Anlagen
          <span className="text-xs text-slate-400">({atts.length}/{MAX})</span>
        </h3>
        <label className="cursor-pointer inline-flex">
          <input type="file" accept="image/*,application/pdf" multiple className="hidden"
                 onChange={(e) => upload(e.target.files)}
                 disabled={uploading || atts.length >= MAX}
                 data-testid="ticket-attachment-input" />
          <span className="text-xs px-3 py-1.5 rounded-md border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1.5">
            <FileArrowUp size={13} /> {uploading ? "Lade hoch…" : "Hochladen"}
          </span>
        </label>
      </div>
      {atts.length === 0 ? (
        <div className="text-xs text-slate-500 py-2">
          Keine Anlagen. Fotos oder PDFs helfen bei der Schadendokumentation.
        </div>
      ) : (
        <div className="grid grid-cols-4 gap-2">
          {atts.map((att) => {
            const isImg = (att.content_type || "").startsWith("image/");
            return (
              <div key={att.id} className="relative group border border-slate-200 rounded-md overflow-hidden bg-slate-50" data-testid={`ticket-attachment-${att.id}`}>
                {isImg ? (
                  urlFor(att) ? (
                    <img
                      src={urlFor(att)} alt={att.filename}
                      className="w-full h-24 object-cover cursor-zoom-in"
                      onClick={() => setLightbox(att)}
                    />
                  ) : (
                    <div className="w-full h-24 flex items-center justify-center bg-slate-100 text-slate-400 text-[10px]">Lade …</div>
                  )
                ) : (
                  urlFor(att) ? (
                    <a href={urlFor(att)} target="_blank" rel="noreferrer"
                       className="flex flex-col items-center justify-center h-24 text-slate-500 hover:bg-slate-100">
                      <ImageIcon size={20} />
                      <span className="text-[10px] mt-1 truncate max-w-[90%]">{att.filename}</span>
                    </a>
                  ) : (
                    <div className="flex flex-col items-center justify-center h-24 text-slate-400">
                      <ImageIcon size={20} />
                      <span className="text-[10px] mt-1 truncate max-w-[90%]">Lade …</span>
                    </div>
                  )
                )}
                <button
                  className="absolute top-1 right-1 bg-white/90 hover:bg-white border border-slate-200 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={() => del(att.id)}
                  title="Anlage löschen"
                  data-testid={`ticket-attachment-delete-${att.id}`}
                >
                  <Trash size={11} className="text-rose-600" />
                </button>
                <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1.5 py-0.5 truncate">
                  {att.filename}
                </div>
              </div>
            );
          })}
        </div>
      )}
      {/* Lightbox */}
      {lightbox && urlFor(lightbox) && (
        <div
          className="fixed inset-0 bg-black/80 z-[9999] flex items-center justify-center p-8 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="ticket-attachment-lightbox"
        >
          <img src={urlFor(lightbox)} alt={lightbox.filename} className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
